import { theme as defaultTheme } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { styled, css } from "styled-components";

export type IndicatorProps = {
  variant:
    | "primary"
    | "secondary"
    | "error"
    | "orange"
    | "transparent"
    | "purple"
    | "pink"
    | "blue"
    | "light-green"
    | string;
  label?: string;
  noMargin?: boolean;
};
const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.label`
  font-size: ${({ theme }) => defaultTheme.FONT.SIZE.STANDARD};
  font-weight: ${({ theme }) => defaultTheme.FONT.WEIGHT.REGULAR};
`;

const InlineContainer = styled.div`
  display: inline-flex;
  align-items: center;
`;

const IndicatedLabel = styled(Typography)`
  font-size: ${({ theme }) => defaultTheme.FONT.SIZE.STANDARD};
  font-weight: ${({ theme }) => defaultTheme.FONT.WEIGHT.REGULAR};
  color: ${({ theme }) => defaultTheme.COLORS.BLUE_DARK};
  padding-right: 6px;
`;

const Indicator = styled.svg<IndicatorProps>`
  height: 10px;
  width: 10px;
  margin-right: ${({ noMargin }) => (noMargin ? 0 : "7px")};

  ${({ theme, variant }) =>
    variant === "primary" && `fill: ${defaultTheme.COLORS.SUCCESS};`};

  ${({ theme, variant }) =>
    variant === "secondary" && `fill: ${defaultTheme.COLORS.SECONDARY};`};

  ${({ theme, variant }) =>
    variant === "error" && `fill: ${defaultTheme.COLORS.ERROR};`};

  ${({ theme, variant }) =>
    variant === "orange" && `fill: ${defaultTheme.COLORS.MINDLER_ORANGE};`};

  ${({ theme, variant }) =>
    variant === "purple" && `fill: ${defaultTheme.COLORS.PURPLE};`};

  ${({ theme, variant }) =>
    variant === "pink" && `fill: ${defaultTheme.COLORS.PINK};`};

  ${({ theme, variant }) =>
    variant === "blue" && `fill: ${defaultTheme.COLORS.BLUE};`};

  ${({ theme, variant }) =>
    variant === "light-green" &&
    `fill: ${defaultTheme.COLORS.VERY_LIGHT_GREEN};`};

  ${({ theme, variant }) =>
    variant === "transparent" &&
    `fill: ${defaultTheme.COLORS.WHITE}; stroke: ${defaultTheme.COLORS.MEDIUM_GREY};`};

  ${({ theme, variant }) =>
    variant === "medium_grey" && `fill: ${defaultTheme.COLORS.MEDIUM_GREY};`};

  ${({ theme, variant }) =>
    variant === "STATUS_GREEN" && `fill: ${defaultTheme.COLORS.STATUS_GREEN};`};

  ${({ theme, variant }) =>
    variant === "STATUS_YELLOW" &&
    `fill: ${defaultTheme.COLORS.STATUS_YELLOW}; `};

  ${({ theme, variant }) =>
    variant === "STATUS_ORANGE" && `fill: ${defaultTheme.COLORS.SECONDARY};`};

  ${({ theme, variant }) =>
    variant === "STATUS_RED" && `fill: ${defaultTheme.COLORS.ERROR};`};

  ${({ variant }) => variant === "hidden" && "fill: none;"};
  ${({ variant }) => variant && variant.match(hexRegex) && `fill: ${variant}`}

  ${({ label }) =>
    label &&
    css`
      margin-right: 4px;
    `}
`;

const StatusIndicator = ({
  variant,
  label = undefined,
  ...props
}: PropsWithChildren<IndicatorProps>) => {
  const { t } = useTranslation();

  if (variant === "STATUS_NEW") {
    return (
      <InlineContainer>
        <IndicatedLabel color="BLUE_DARK" variant="body-small" isDemo={false}>
          {t("NEW")}
        </IndicatedLabel>
      </InlineContainer>
    );
  }

  if (label) {
    return (
      <Container>
        <Indicator height="10" width="10" variant={variant} {...props}>
          <circle cx="5" cy="5" r="4" />
        </Indicator>
        <Label>{label}</Label>
      </Container>
    );
  }

  return (
    <>
      <Indicator height="10" width="10" variant={variant} {...props}>
        <circle cx="5" cy="5" r="4" />
      </Indicator>
    </>
  );
};

export default StatusIndicator;
